import { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { DevComponents } from "./pages/DevComponents";
import { Login } from "./pages/Login";
import { ForgotPassword } from "./pages/Auth/ForgotPassword";
import { ResetPassword } from "./pages/Auth/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { Security } from "./pages/Settings/Security";
import { Profile } from "./pages/Settings/Profile";
import { Settings } from "./pages/Settings";
import { Language } from "./pages/Settings/Language";
import { SetupTwoFactorRequired } from "./components/pages/SetupTwoFactorRequired";
import { ProtectedRoute } from "./components/organisms/ProtectedRoute";
import { AdminRoute } from "./components/organisms/AdminRoute";
import { ErrorBoundary } from "./components/organisms/ErrorBoundary";
import { MainLayout } from "./components/layout/MainLayout";
import { InvestorLayout } from "./components/layout/InvestorLayout";
import { StaffViewSelector } from "./components/organisms/StaffViewSelector";
import { StaffDeliveryRoutes } from "./components/routing/StaffDeliveryRoutes";
import { StaffOperationsRoutes } from "./components/routing/StaffOperationsRoutes";
import { useAuthStore } from "./store/authStore";
import { SentryRouter } from "./config/sentry";

// Lazy load Investments page for better initial bundle size
const Investments = lazy(() =>
  import("./pages/Investments").then((m) => ({ default: m.Investments }))
);
// Lazy load Investment detail page
const InvestmentDetail = lazy(() =>
  import("./pages/InvestmentDetail").then((m) => ({ default: m.InvestmentDetail }))
);
// Lazy load Rebalancing page (Story 9-1)
const Rebalancing = lazy(() =>
  import("./pages/Rebalancing").then((m) => ({ default: m.Rebalancing }))
);
// Lazy load Investor ROI Report (Story 9-2)
const InvestorRoiReport = lazy(() =>
  import("./pages/InvestorRoiReport").then((m) => ({ default: m.InvestorRoiReport }))
);
// Lazy load Investor Dashboard (Story 9-3)
const InvestorDashboard = lazy(() =>
  import("./pages/Investor/InvestorDashboard").then((m) => ({ default: m.InvestorDashboard }))
);
const InvestorDashboardAdmin = lazy(() =>
  import("./pages/Investor/InvestorDashboardAdmin").then((m) => ({
    default: m.InvestorDashboardAdmin,
  }))
);
// Lazy load Reports hub and placeholders (Story 10-1)
const ReportsDashboard = lazy(() =>
  import("./pages/Reports/ReportsDashboard").then((m) => ({ default: m.ReportsDashboard }))
);
const ReportPlaceholder = lazy(() =>
  import("./pages/Reports/ReportPlaceholder").then((m) => ({ default: m.ReportPlaceholder }))
);
// Story 10-2: Financial report pages
const CashFlowReport = lazy(() =>
  import("./pages/Reports/CashFlowReport").then((m) => ({ default: m.CashFlowReport }))
);
const CostAnalysisReport = lazy(() =>
  import("./pages/Reports/CostAnalysisReport").then((m) => ({ default: m.CostAnalysisReport }))
);
const BurnRateReport = lazy(() =>
  import("./pages/Reports/BurnRateReport").then((m) => ({ default: m.BurnRateReport }))
);
const ProfitMarginReport = lazy(() =>
  import("./pages/Reports/ProfitMarginReport").then((m) => ({ default: m.ProfitMarginReport }))
);
const RevenueByPeriodReport = lazy(() =>
  import("./pages/Reports/RevenueByPeriodReport").then((m) => ({
    default: m.RevenueByPeriodReport,
  }))
);
const ExpensesByPeriodReport = lazy(() =>
  import("./pages/Reports/ExpensesByPeriodReport").then((m) => ({
    default: m.ExpensesByPeriodReport,
  }))
);
const SalesVelocityReport = lazy(() =>
  import("./pages/Reports/SalesVelocityReport").then((m) => ({ default: m.SalesVelocityReport }))
);
// Story 10-5: Order Performance reports
const OrderCompletionReport = lazy(() =>
  import("./pages/Reports/OrderCompletionReport").then((m) => ({
    default: m.OrderCompletionReport,
  }))
);
const DeliveryPerformanceReport = lazy(() =>
  import("./pages/Reports/DeliveryPerformanceReport").then((m) => ({
    default: m.DeliveryPerformanceReport,
  }))
);
// Story 10-6: Customer Intelligence reports
const TopCustomersReport = lazy(() =>
  import("./pages/Reports/TopCustomersReport").then((m) => ({ default: m.TopCustomersReport }))
);
const CancellationPatternsReport = lazy(() =>
  import("./pages/Reports/CancellationPatternsReport").then((m) => ({
    default: m.CancellationPatternsReport,
  }))
);
// Story 10-7: Inventory Health report
const InventoryHealthReport = lazy(() =>
  import("./pages/Reports/InventoryHealthReport").then((m) => ({
    default: m.InventoryHealthReport,
  }))
);
// Story 10-8: Purchase Patterns report
const PurchasePatternsReport = lazy(() =>
  import("./pages/Reports/PurchasePatternsReport").then((m) => ({
    default: m.PurchasePatternsReport,
  }))
);
// Story 11.6-4: Receivables report (flat unpaid-orders list)
const ReceivablesReport = lazy(() =>
  import("./pages/Reports/ReceivablesReport").then((m) => ({
    default: m.ReceivablesReport,
  }))
);
// Lazy load Inventory page
const Inventory = lazy(() => import("./pages/Inventory").then((m) => ({ default: m.Inventory })));
const InventoryDetail = lazy(() =>
  import("./pages/InventoryDetail").then((m) => ({ default: m.InventoryDetail }))
);
// Lazy load Catalog page (Story 10.5-1)
const Catalog = lazy(() => import("./pages/Catalog"));
const CatalogDetail = lazy(() => import("./pages/CatalogDetail"));
// Lazy load Production page (Story 10.5-5)
const ProductionHistory = lazy(() =>
  import("./pages/ProductionHistory").then((m) => ({ default: m.ProductionHistory }))
);
// Lazy load Customers page
const Customers = lazy(() => import("./pages/Customers").then((m) => ({ default: m.Customers })));
const CustomerDetail = lazy(() =>
  import("./pages/CustomerDetail").then((m) => ({ default: m.CustomerDetail }))
);
// Lazy load Suppliers page (Story 5.1)
const Suppliers = lazy(() => import("./pages/Suppliers").then((m) => ({ default: m.Suppliers })));
const SupplierDetail = lazy(() =>
  import("./pages/SupplierDetail").then((m) => ({ default: m.SupplierDetail }))
);
// Lazy load Orders page
const Orders = lazy(() => import("./pages/Orders").then((m) => ({ default: m.Orders })));
// Lazy load Purchases page (Story 5.3)
const Purchases = lazy(() => import("./pages/Purchases").then((m) => ({ default: m.Purchases })));
// Lazy load Alerts page (Story 6.2)
const Alerts = lazy(() => import("./pages/Alerts").then((m) => ({ default: m.Alerts })));
// Lazy load Users page (Story 6-6-4 – admin/manager only)
const Users = lazy(() => import("./pages/Users").then((m) => ({ default: m.Users })));
// Lazy load Order detail page (Story 4.5)
const OrderDetail = lazy(() =>
  import("./pages/OrderDetail").then((m) => ({ default: m.OrderDetail }))
);
// Portal (Story 7-1)
const PortalLogin = lazy(() =>
  import("./pages/Portal/PortalLogin").then((m) => ({ default: m.PortalLogin }))
);
const PortalDashboard = lazy(() =>
  import("./pages/Portal/PortalDashboard").then((m) => ({ default: m.PortalDashboard }))
);
const PortalForgotPassword = lazy(() =>
  import("./pages/Portal/PortalForgotPassword").then((m) => ({ default: m.PortalForgotPassword }))
);
const PortalRequestAccess = lazy(() =>
  import("./pages/Portal/PortalRequestAccess").then((m) => ({ default: m.PortalRequestAccess }))
);
const PortalResetPassword = lazy(() =>
  import("./pages/Portal/PortalResetPassword").then((m) => ({ default: m.PortalResetPassword }))
);
const PortalProducts = lazy(() =>
  import("./pages/Portal/PortalProducts").then((m) => ({ default: m.PortalProducts }))
);
const PortalOrders = lazy(() =>
  import("./pages/Portal/PortalOrders").then((m) => ({ default: m.PortalOrders }))
);
const PortalOrderDetail = lazy(() =>
  import("./pages/Portal/PortalOrderDetail").then((m) => ({ default: m.PortalOrderDetail }))
);

/** Shared fallback for lazy routes. Default: full-screen spinner; sm: compact (e.g. portal). */
function RouteFallback({ size = "default" }: { size?: "default" | "sm" }) {
  const isSm = size === "sm";
  return (
    <div className={`flex items-center justify-center ${isSm ? "min-h-[200px]" : "min-h-screen"}`}>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary-600 ${isSm ? "h-8 w-8" : "h-12 w-12"}`}
      />
    </div>
  );
}

function AuthenticatedApp() {
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Staff-only shell: choose between delivery view and operations view (Story 10-5-8)
  if (user?.role === "staff") {
    const [viewPreference, setViewPreference] = useState<string | null>(() =>
      localStorage.getItem("staff_view_preference")
    );
    const [showSelector, setShowSelector] = useState(!viewPreference);

    const handleViewSelect = (view: "delivery" | "operations") => {
      setViewPreference(view);
      setShowSelector(false);
      navigate(view === "delivery" ? "/staff" : "/dashboard");
    };

    if (showSelector) {
      return <StaffViewSelector onSelect={handleViewSelect} />;
    }

    if (viewPreference === "delivery") {
      return <StaffDeliveryRoutes />;
    } else {
      return <StaffOperationsRoutes />;
    }
  }

  // Investor-only shell: only /investor and /investor/dashboard, /investor/settings (Story 9-3)
  if (user?.role === "investor") {
    return (
      <ErrorBoundary>
        <Routes>
          <Route
            path="/investor"
            element={
              <InvestorLayout>
                <Suspense fallback={<RouteFallback />}>
                  <InvestorDashboard />
                </Suspense>
              </InvestorLayout>
            }
          />
          <Route
            path="/investor/dashboard"
            element={
              <InvestorLayout>
                <Suspense fallback={<RouteFallback />}>
                  <InvestorDashboard />
                </Suspense>
              </InvestorLayout>
            }
          />
          <Route
            path="/investor/settings"
            element={
              <InvestorLayout>
                <Settings />
              </InvestorLayout>
            }
          />
          <Route
            path="/investor/settings/profile"
            element={
              <InvestorLayout>
                <Profile />
              </InvestorLayout>
            }
          />
          <Route
            path="/investor/settings/security"
            element={
              <InvestorLayout>
                <Security />
              </InvestorLayout>
            }
          />
          <Route
            path="/investor/settings/language"
            element={
              <InvestorLayout>
                <Language />
              </InvestorLayout>
            }
          />
          <Route
            path="*"
            element={
              <Navigate to="/investor" state={{ from: location, accessDenied: true }} replace />
            }
          />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <MainLayout>
      <ErrorBoundary>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/investments"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Investments />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/investments/rebalancing"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Rebalancing />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/investments/roi"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InvestorRoiReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/investments/investor-dashboard"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InvestorDashboardAdmin />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/investments/:id"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InvestmentDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ReportsDashboard />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/cash-flow"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CashFlowReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/cost-analysis"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CostAnalysisReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/burn-rate"
            element={
              <AdminRoute allowedRoles={["admin"]}>
                <Suspense fallback={<RouteFallback />}>
                  <BurnRateReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/profit-margin"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ProfitMarginReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/revenue-by-period"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <RevenueByPeriodReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/financial/expenses-by-period"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ExpensesByPeriodReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/operational/sales-velocity"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <SalesVelocityReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/operational/order-completion"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <OrderCompletionReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/operational/delivery-performance"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <DeliveryPerformanceReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/customers/top-customers"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <TopCustomersReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/customers/cancellation-patterns"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CancellationPatternsReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/inventory/health"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InventoryHealthReport />
                </Suspense>
              </AdminRoute>
            }
          />
          {/* Story 10-8: Purchase Patterns is categorized under Inventory & Purchasing in Reports Hub (see ReportsDashboard.tsx) */}
          <Route
            path="/reports/inventory/purchase-patterns"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <PurchasePatternsReport />
                </Suspense>
              </AdminRoute>
            }
          />
          {/* Story 11.6-4: flat route (not nested under a category), per the story's own route spec */}
          <Route
            path="/reports/receivables"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ReceivablesReport />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/reports/:category/:reportSlug"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ReportPlaceholder />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Inventory />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/inventory/:id"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InventoryDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/catalog"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Catalog />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/catalog/:id"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CatalogDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/production"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ProductionHistory />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Customers />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CustomerDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Suppliers />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/suppliers/:id"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <SupplierDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff", "customer"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Orders />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff", "customer"]}>
                <Suspense fallback={<RouteFallback />}>
                  <OrderDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/purchases"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Purchases />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <AdminRoute allowedRoles={["admin", "manager", "staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Alerts />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute allowedRoles={["admin", "manager"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Users />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route path="/portal" element={<Navigate to="/portal/products" replace />} />
          <Route
            path="/portal/dashboard"
            element={
              <AdminRoute allowedRoles={["customer"]}>
                <Suspense fallback={<RouteFallback size="sm" />}>
                  <PortalDashboard />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/portal/products"
            element={
              <AdminRoute allowedRoles={["customer"]}>
                <Suspense fallback={<RouteFallback size="sm" />}>
                  <PortalProducts />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/portal/orders"
            element={
              <AdminRoute allowedRoles={["customer"]}>
                <Suspense fallback={<RouteFallback size="sm" />}>
                  <PortalOrders />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/portal/orders/:id"
            element={
              <AdminRoute allowedRoles={["customer"]}>
                <Suspense fallback={<RouteFallback size="sm" />}>
                  <PortalOrderDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/profile" element={<Profile />} />
          <Route path="/settings/security" element={<Security />} />
          <Route path="/settings/language" element={<Language />} />
          {import.meta.env.DEV && <Route path="/dev/components" element={<DevComponents />} />}
          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </MainLayout>
  );
}

function App() {
  const { isLoading, checkAuth } = useAuthStore();
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    checkAuth().catch(() => {
      setAuthError(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-neutral-50 dark:bg-neutral-900">
        <p className="text-danger-600 dark:text-danger-400">
          Failed to check authentication. Please refresh the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <SentryRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/portal/login"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PortalLogin />
            </Suspense>
          }
        />
        <Route
          path="/portal/request-access"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PortalRequestAccess />
            </Suspense>
          }
        />
        <Route
          path="/portal/forgot-password"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PortalForgotPassword />
            </Suspense>
          }
        />
        <Route
          path="/portal/reset-password"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PortalResetPassword />
            </Suspense>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/setup-2fa-required"
          element={
            <ProtectedRoute>
              <SetupTwoFactorRequired />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AuthenticatedApp />
            </ProtectedRoute>
          }
        />
      </Routes>
    </SentryRouter>
  );
}

export default App;
